import type { LogicalType } from '@/types'
import type { DataTypeDefinition, DialectAdapter, DialectWarning } from '../dialectTypes'

const DATA_TYPES: DataTypeDefinition[] = [
  { value: 'TINYINT',       label: 'TINYINT',       logicalType: 'smallint' },
  { value: 'SMALLINT',      label: 'SMALLINT',      logicalType: 'smallint' },
  { value: 'INT',           label: 'INT',            logicalType: 'integer' },
  { value: 'BIGINT',        label: 'BIGINT',         logicalType: 'bigint' },
  { value: 'DECIMAL(10,2)', label: 'DECIMAL(p,s)',   logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'NUMERIC(10,2)', label: 'NUMERIC(p,s)',   logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'FLOAT',         label: 'FLOAT',          logicalType: 'float' },
  { value: 'DOUBLE',        label: 'DOUBLE',         logicalType: 'float' },
  { value: 'VARCHAR(255)',  label: 'VARCHAR(n)',      logicalType: 'string', hasLength: true },
  { value: 'CHAR(1)',       label: 'CHAR(n)',         logicalType: 'string', hasLength: true },
  { value: 'TEXT',          label: 'TEXT',            logicalType: 'text' },
  { value: 'MEDIUMTEXT',    label: 'MEDIUMTEXT',      logicalType: 'text' },
  { value: 'LONGTEXT',      label: 'LONGTEXT',        logicalType: 'text' },
  { value: 'TINYINT(1)',    label: 'TINYINT(1) (bool)',logicalType: 'boolean' },
  { value: 'DATE',          label: 'DATE',            logicalType: 'date' },
  { value: 'TIME',          label: 'TIME',            logicalType: 'time' },
  { value: 'DATETIME',      label: 'DATETIME',        logicalType: 'datetime' },
  { value: 'TIMESTAMP',     label: 'TIMESTAMP',       logicalType: 'datetime' },
  { value: 'CHAR(36)',      label: 'CHAR(36) (UUID)', logicalType: 'uuid' },
  { value: 'JSON',          label: 'JSON',            logicalType: 'json' },
  { value: 'BLOB',          label: 'BLOB',            logicalType: 'binary' },
]

const LOGICAL_TO_PHYSICAL: Record<LogicalType, string> = {
  string:   'VARCHAR(255)',
  text:     'TEXT',
  integer:  'INT',
  bigint:   'BIGINT',
  smallint: 'SMALLINT',
  decimal:  'DECIMAL(10,2)',
  float:    'DOUBLE',
  boolean:  'TINYINT(1)',
  date:     'DATE',
  time:     'TIME',
  datetime: 'DATETIME',
  uuid:     'CHAR(36)',
  json:     'JSON',
  binary:   'BLOB',
}

function normalizeTypeKey(type: string): string {
  return type.trim().toUpperCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
}

export const mysqlDialect: DialectAdapter = {
  id: 'mysql',
  label: 'MySQL / MariaDB',
  dataTypes: DATA_TYPES,

  mapLogicalToPhysical(type) {
    return LOGICAL_TO_PHYSICAL[type] ?? 'TEXT'
  },

  mapPhysicalToLogical(type) {
    const key = normalizeTypeKey(type)
    switch (key) {
      case 'TINYINT':
      case 'SMALLINT':
        return 'smallint'
      case 'INT':
      case 'MEDIUMINT':
        return 'integer'
      case 'BIGINT':
        return 'bigint'
      case 'DECIMAL':
      case 'NUMERIC':
        return 'decimal'
      case 'FLOAT':
      case 'DOUBLE':
      case 'REAL':
        return 'float'
      case 'VARCHAR':
      case 'CHAR':
        return 'string'
      case 'TEXT':
      case 'MEDIUMTEXT':
      case 'LONGTEXT':
      case 'TINYTEXT':
        return 'text'
      case 'DATE':
        return 'date'
      case 'TIME':
        return 'time'
      case 'DATETIME':
      case 'TIMESTAMP':
        return 'datetime'
      case 'JSON':
        return 'json'
      case 'BLOB':
      case 'MEDIUMBLOB':
      case 'LONGBLOB':
      case 'BINARY':
      case 'VARBINARY':
        return 'binary'
      default:
        return 'text'
    }
  },

  validateColumn(column) {
    const warnings: DialectWarning[] = []
    const upper = column.type.trim().toUpperCase()
    if (upper === 'JSONB') {
      warnings.push({ column: column.name, message: 'JSONB is not supported in MySQL; use JSON instead.', severity: 'warning' })
    }
    if (upper === 'UUID') {
      warnings.push({ column: column.name, message: 'UUID is not a native MySQL type; use CHAR(36) instead.', severity: 'warning' })
    }
    return warnings
  },
}

// MariaDB is close enough to share the adapter with a label override
export const mariadbDialect: DialectAdapter = {
  ...mysqlDialect,
  id: 'mariadb',
  label: 'MariaDB',
}
